import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CheckIcon from "@mui/icons-material/Check";
import { useLocation } from "react-router-dom"; // ✅ added

const theme = createTheme({
  palette: {
    primary: {
      main: "#22ccb2",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
  },
});

const influencerContent = [
  {
    title: "📢 About Sevenly Travel",
    items: [
      "A socially conscious OTA that turns every hotel booking into a force for good.",
      "Bookings support charitable causes — at the best available rates.",
    ],
  },
  {
    title: "🌟 What is the Sevenly Stars Program?",
    items: [
      "Exclusive affiliate initiative for influencers passionate about travel and social impact.",
      "Earn commission on each completed booking through your personalized link.",
    ],
  },
  {
    title: "🎁 Perks of Being a Sevenly Star",
    items: [
      "Earn Commission — including on your own trips!",
      "$250 Monthly Travel Credit to use and promote experiences.",
      "Exposure across Sevenly’s social media and explore page.",
      "VIP perks — upgrades, giveaways, exclusive deals.",
      "Annual Sevenly Star Retreat — connect and co-create.",
      "Make Impact — support global charities with every booking.",
    ],
  },
  {
    title: "⚙️ How It Works",
    items: [
      "Apply & Get Approved",
      "Receive Your Unique Booking Link",
      "Share Hotel Deals & Itineraries",
      "Track Bookings, Earn Commission, Create Impact",
    ],
  },
  {
    title: "💰 Commission Details",
    items: [
      "Industry-leading payouts per completed hotel stay.",
      "Transparent monthly reporting.",
      "No cap on earnings.",
      "Bonus tiers for top performers.",
    ],
  },
  {
    title: "🤝 Who We’re Looking For",
    items: [
      "Creators who love travel and hidden gems.",
      "Engaged audiences that trust their recommendations.",
      "Values of intentional living, generosity, purpose.",
      "Looking to monetize while doing good.",
    ],
  },
  {
    title: "🌍 Real Impact",
    items: [
      "Education access",
      "Fight against human trafficking",
      "Clean water initiatives",
      "Mental health support",
      "Wildlife conservation",
    ],
  },
];

const b2bContent = [
  {
    title: "🏨 What You Get Access To",
    items: [
      "Real-time hotel availability and pricing",
      "Access to 1 million hotels",
      "Discounted rates across major cities and hidden gems",
      "Charity-linked booking incentives for your users",
      "Custom filters: geo, amenity, price, and cause proximity",
    ],
  },
  {
    title: "💸 How You Earn",
    items: [
      "Earn commission on every completed booking",
      "White-label and co-branded options available",
      "Transparent monthly payout reports",
      "Flexible commission structures for high-volume partners",
    ],
  },
  {
    title: "🌟 Why Partner with Us?",
    items: [
      "Mission-Driven Model — Users support causes without paying more",
      "Developer-Friendly API — Seamlessly integrates with clear docs",
      "Experience Layer — Add itineraries to boost conversions",
      "Scalable — Built for startups and enterprises alike",
    ],
  },
  {
    title: "⚙️ How It Works",
    items: [
      "Apply for API Access",
      "Get Approved + Onboarded",
      "Launch Your Integration",
      "Track Performance + Get Paid",
    ],
  },
  {
    title: "📘 Developer Tools",
    items: [
      "RESTful API with JSON responses",
      "Sandbox and staging environment",
      "Filters by location, rating, price, and cause",
      "Dynamic pricing and availability",
      "Dedicated dev support",
    ],
  },
];

const AffiliateProgram = () => {
  const location = useLocation(); // ✅ added
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    details: "",
    honeypot: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isInfluencer, setIsInfluencer] = useState(false);

  useEffect(() => {
    setIsInfluencer(location.pathname.includes("influencer"));
  }, [location.pathname]); // ✅ now updates on route change

  const sections = isInfluencer ? influencerContent : b2bContent;

  const validateForm = () => {
    const { name, email, details, honeypot, company, phone } = formData;
    if (honeypot) return false;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!name.trim() || !emailPattern.test(email) || !details.trim())
      return false;
    if (!isInfluencer && (!company.trim() || !phone.trim())) return false;
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedValue =
      name === "phone" ? value.replace(/[^0-9]/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setError(true);
      setMessage("Please fill out all required fields correctly.");
      return;
    }

    setIsSubmitting(true);
    setMessage("");
    setError(false);

    try {
      const apiUrl = isInfluencer
        ? process.env.REACT_APP_BACKEND + "affiliate/influencer"
        : process.env.REACT_APP_BACKEND + "affiliate/b2b";

      const payload = isInfluencer
        ? {
            name: formData.name,
            email: formData.email,
            aboutYou: formData.details,
          }
        : {
            name: formData.name,
            email: formData.email,
            company: formData.company,
            phone: formData.phone,
            details: formData.details,
          };

      await axios.post(apiUrl, payload);
      setMessage("Your request has been submitted successfully!");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        details: "",
        honeypot: "",
      });
    } catch (err) {
      const serverMessage =
        err.response?.data?.message ||
        "Error submitting request. Please try again.";
      setMessage(serverMessage);
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "#f9f9f9", minHeight: "100vh", pt: 6, pb: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant={isMobile ? "h4" : "h3"} gutterBottom>
              {isInfluencer
                ? "Sevenly Stars Influencer Program"
                : "Sevenly Travel Affiliate Program"}
            </Typography>
            <Typography color="text.secondary">
              {isInfluencer
                ? "Inspire your audience. Earn commissions. Drive impact."
                : "Distribute hotel inventory. Earn commissions. Fuel social impact."}
            </Typography>
            <Typography fontWeight="bold" color="primary.main">
              Travel that feels good and does good.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {sections.map((section, index) => (
              <Grid item key={index} xs={12} sm={10} md={6}>
                <Paper elevation={1} sx={{ p: 4, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {section.title}
                  </Typography>
                  <List disablePadding>
                    {section.items.map((item, idx) => (
                      <ListItem
                        key={idx}
                        disableGutters
                        sx={{ alignItems: "start", pl: 0, pr: 0 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ variant: "body1" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={8} mb={6}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {isInfluencer
                ? "Become a Part of the Sevenly Star Movement"
                : "Ready to Distribute Impact-Driven Travel?"}
            </Typography>
            <Typography color="text.secondary" mb={2}>
              {isInfluencer
                ? "Join creators who turn travel into purpose-driven change."
                : "Join partners transforming hotel bookings into a force for good."}
            </Typography>
          </Box>

          <Box id="form" maxWidth={500} mx="auto">
            <Typography
              variant="h5"
              align="center"
              fontWeight="bold"
              gutterBottom
            >
              {isInfluencer
                ? "Apply to Become a Sevenly Star"
                : "Apply for API Access"}
            </Typography>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <form onSubmit={handleSubmit} autoComplete="off">
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  {!isInfluencer && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={isInfluencer ? "About You" : "Details"}
                      name="details"
                      value={formData.details}
                      onChange={handleChange}
                      required
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </Button>
                  </Grid>
                </Grid>
                {message && (
                  <Alert severity={error ? "error" : "success"} sx={{ mt: 2 }}>
                    {message}
                  </Alert>
                )}
              </form>
            </Paper>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AffiliateProgram;
